import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { profileApprovalColumns } from "../../tables/profileApprovalColumns";
import AdminDataTable from "../../components/common/AdminDataTable";
import ViewPhotoPopup from "./ViewPhotoPopup";
import toast from "../../utils/toast";

export default function ProfileApproval() {
  const PAGE_SIZE = 10;
  const [searchParams] = useSearchParams();
  const searchParamString = searchParams.toString();
  const latestRequestRef = useRef(0);

  const normalizePendingFilter = (value) => {
    const allowed = ["all", "profilePending", "genderPending", "aboutMePending"];
    return allowed.includes(value) ? value : "all";
  };
  
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setListLoading] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [member, setMember] = useState([]);
  const [pendingFilter, setPendingFilter] = useState("all");
  const [appliedPendingFilter, setAppliedPendingFilter] = useState("all");
  const [counts, setCounts] = useState({
    profilePending: 0,
    genderPending: 0,
    aboutMePending: 0,
  });
  const [filteredCount, setFilteredCount] = useState(0);

  // filters
  const [profileId, setProfileId] = useState("");
  const [name, setName] = useState("");
  const [emailMobile, setEmailMobile] = useState("");

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";

  const fetchMember = async (p = null, pSize = null, pendingFilterOverride = null) => {
    const curPage = p !== null ? p : page;
    const curPageSize = pSize !== null ? pSize : pageSize;
    const currentPendingFilter = pendingFilterOverride !== null ? pendingFilterOverride : appliedPendingFilter;
    const requestId = ++latestRequestRef.current;
    setListLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: curPage,
        limit: curPageSize,
        sortBy,
        sortOrder,
        profile_id: profileId,
        name,
        emailMobile,
        pendingFilter: currentPendingFilter,
      }).toString();

      const response = await fetch(
        `${API_URL}/api/admin/profile-management/get-profile-approval?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (result.success) {
        setMember(result.data || []);
        const totalCount = result.totalCount || 0;
        setFilteredCount(totalCount);
        setCounts(result.counts || {
          profilePending: 0,
          genderPending: 0,
          aboutMePending: 0,
        });
        setTotalPages(Math.max(1, Math.ceil(totalCount / curPageSize)));
      } else {
        setMember([]);
        setFilteredCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) {
        return;
      }
      console.log("Error fetching members:", error);
    } finally {
      if (requestId === latestRequestRef.current) {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchMember();
  }, [page, pageSize, sortBy, sortOrder, appliedPendingFilter]);

  // Sync query filter whenever URL search params change.
  useEffect(() => {
    const params = new URLSearchParams(searchParamString);
    const filterParam = normalizePendingFilter(params.get("filter") || "all");

    setPendingFilter(filterParam);
    setAppliedPendingFilter(filterParam);
    setPage(1);
  }, [searchParamString]);

  const updatePhoto = async (row, status, type) => {
  try {
    let remark = "";

    if (status === "Rejected") {
      const input = window.prompt("Please enter rejection remark");
      if (input === null) {
        fetchMember();
        return;
      }

      remark = input.trim();
      if (!remark) {
        toast.error("Remark is required for rejection");
        fetchMember();
        return;
      }
    }

    const response = await fetch(
      `${API_URL}/api/admin/profile-management/profile-approval-request`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_id: row._id,
          type: type,              
          status: status,
          remark,
        })
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update status");
    }

    const data = await response.json();
    console.log("Status updated:", data);

    fetchMember(); // refresh table
  } catch (error) {
    console.error("Error updating status:", error);
  }
};

  const handlePhotoStatusChange = async (row, status) => {
    updatePhoto(row, status, "photo");
  };

  const handleProfileStatusChange = async (row, status, actionType = "profile") => {
    updatePhoto(row, status, actionType);
  };

  
   const openModal = (row) => {
     setMemberData(row);
     setIsOpen(true);
   };

   const closeModal = () => {
     setIsOpen(false);
     setMemberData(null);
   };

  const columns = profileApprovalColumns({ PAGE_SIZE, handlePhotoStatusChange, handleProfileStatusChange, viewPhoto: openModal });

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Profile Approval" />

        <div className="right_col" role="main">
          

          <div className="x_panel">
            <div className="x_title">
              <h2>
                <span style={{ color: "red" }}>Profile Pending: ({counts.profilePending || 0})</span> |{" "}
                <span style={{ color: "red" }}>Gender Pending: ({counts.genderPending || 0})</span> |{" "}
                <span style={{ color: "red" }}>About Me Pending: ({counts.aboutMePending || 0})</span> |{" "}
                <span style={{ color: "#333" }}>Current Filter Count: ({filteredCount || 0})</span>
              </h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              {/* FILTER */}
              <div className="filtercont">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="row">
                      <div className="col-sm-2">
                        <label>Profile ID</label>
                        <input
                          type="text"
                          className="form-control"
                          value={profileId}
                          onChange={(e) => setProfileId(e.target.value)}
                        />
                      </div>

                      <div className="col-sm-2">
                        <label>Search By Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>

                      <div className="col-sm-2">
                        <label>Email / Mobile</label>
                        <input
                          type="text"
                          className="form-control"
                          value={emailMobile}
                          onChange={(e) => setEmailMobile(e.target.value)}
                        />
                      </div>

                    <div className="col-sm-2">
                        <label>All </label>
                        <select
                          className="form-control"
                          value={pendingFilter}
                          onChange={(e) => {
                            setPendingFilter(e.target.value);
                          }}
                        >
                            <option value="all">All</option>
                            <option value="profilePending">Profile Pending</option>
                            <option value="genderPending">Gender Pending</option>
                            <option value="aboutMePending">About Me Pending</option>
                        </select> 
                      </div>

                      <div className="col-sm-1">
                        <label>&nbsp;</label>
                        <button
                          className="btn btn-default submit sitebtn px-3"
                          onClick={() => {
                            const selectedFilter = pendingFilter;
                            setAppliedPendingFilter(selectedFilter);
                            setPage(1);
                            fetchMember(1, pageSize, selectedFilter);
                          }}
                        >
                          Search
                        </button>
                       
                      </div>
                      <div className="col-sm-1">
                        <label>&nbsp;</label>
                         <button
                          className="btn btn-default submit sitebtn px-3"
                          onClick={() => {
                            setProfileId("");
                            setName("");
                            setEmailMobile("");
                            setPendingFilter("all");
                            setAppliedPendingFilter("all");
                            setPage(1);
                            fetchMember(1, pageSize, "all");
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* TABLE */}
              <AdminDataTable
                columns={columns}
                data={member}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={loading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);  
                  setPage(1);         
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />

               {isOpen && memberData && (
                 <ViewPhotoPopup
                   key={memberData?._id || "profile-approval-photo-modal"}
                   memberData={memberData}
                   isOpen={isOpen}
                   closePopup={closeModal}
                   onPhotoStatusChange={fetchMember}
                 />
               )}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
